import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Settings,
  Edit,
  Eye,
  BarChart3,
  Activity,
  Info,
  Target,
  Plus,
  Trash2,
  UserCheck,
  X,
  Calculator,
  RefreshCw
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import EventStatistics from '../kpis/EventStatistics';
import BettingCalculator from '../calculator/BettingCalculator';
import EventLiquidation from './EventLiquidation';
import EventMatches from './EventMatches';

const EventDetails = ({ eventId, onEdit = null }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [editingSeedUserId, setEditingSeedUserId] = useState(null);
  const [editingSeedValue, setEditingSeedValue] = useState('');
  const queryClient = useQueryClient();

  const { data: event, isLoading, error } = useQuery(
    ['event', eventId],
    () => api.get(`/events/${eventId}`).then(res => res.data),
    {
      enabled: !!eventId,
      staleTime: 30000
    }
  );

  // Query para obtener todos los clientes disponibles
  const { data: allClients } = useQuery(
    ['clients', 'all'],
    () => api.get('/users', { params: { limit: 1000 } }).then(res => res.data),
    {
      staleTime: 60000
    }
  );

  const updateStatusMutation = useMutation(
    ({ eventId, status }) => api.put(`/events/${eventId}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('admin-dashboard');
        toast.success('Estado del evento actualizado');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar evento');
      }
    }
  );

  // Mutation para agregar participante
  const addParticipantMutation = useMutation(
    ({ eventId, userId, seedAmount }) =>
      api.post(`/events/${eventId}/participants`, { userId, seedAmount }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('events');
        toast.success('Participante agregado exitosamente');
        setShowAddParticipant(false);
        setSelectedClients([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al agregar participante');
      }
    }
  );

  // Mutation para remover participante
  const removeParticipantMutation = useMutation(
    ({ eventId, userId }) =>
      api.delete(`/events/${eventId}/participants/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('events');
        toast.success('Participante removido exitosamente');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al remover participante');
      }
    }
  );

  // Mutation para reactivar participante
  const reactivateParticipantMutation = useMutation(
    ({ eventId, userId }) =>
      api.put(`/events/${eventId}/participants/${userId}/reactivate`),
    {
      onSuccess: (response) => {
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('events');
        toast.success(response.data.message || 'Participante reactivado exitosamente');
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al reactivar participante';
        const details = error.response?.data;

        if (details?.shortfall) {
          toast.error(`${errorMsg}\nFaltante: $${details.shortfall.toLocaleString()}`);
        } else {
          toast.error(errorMsg);
        }
      }
    }
  );

  // Mutation para actualizar semilla
  const updateSeedMutation = useMutation(
    ({ eventId, userId, seedAmount }) =>
      api.put(`/events/${eventId}/participants/${userId}/seed`, { seed_amount: seedAmount }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['event', eventId]);
        queryClient.invalidateQueries('events');
        setEditingSeedUserId(null);
        setEditingSeedValue('');
        toast.success('Semilla actualizada exitosamente');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar semilla');
      }
    }
  );

  const handleUpdateSeed = (userId) => {
    const seedAmount = parseFloat(editingSeedValue);
    if (!seedAmount || seedAmount <= 0) {
      toast.error('La semilla debe ser mayor a 0');
      return;
    }
    updateSeedMutation.mutate({ eventId, userId, seedAmount });
  };

  const handleStatusChange = (newStatus) => {
    updateStatusMutation.mutate({ eventId, status: newStatus });
  };

  const handleAddParticipants = () => {
    if (selectedClients.length === 0) {
      toast.error('Selecciona al menos un cliente');
      return;
    }

    // Agregar cada cliente seleccionado
    selectedClients.forEach(client => {
      addParticipantMutation.mutate({
        eventId,
        userId: client.id,
        seedAmount: client.seed_amount || 1000
      });
    });
  };

  const handleRemoveParticipant = (userId) => {
    if (window.confirm('¿Estás seguro de que quieres remover este participante?')) {
      removeParticipantMutation.mutate({ eventId, userId });
    }
  };

  const handleReactivateParticipant = (userId) => {
    if (window.confirm('¿Estás seguro de que quieres reactivar este participante? Se verificará que tenga saldo suficiente.')) {
      reactivateParticipantMutation.mutate({ eventId, userId });
    }
  };

  const toggleClientSelection = (client) => {
    setSelectedClients(prev => {
      const isSelected = prev.some(c => c.id === client.id);
      if (isSelected) {
        return prev.filter(c => c.id !== client.id);
      } else {
        return [...prev, { ...client, seed_amount: client.seed_amount || 1000 }];
      }
    });
  };

  const updateClientSeedAmount = (clientId, seedAmount) => {
    setSelectedClients(prev =>
      prev.map(client =>
        client.id === clientId ? { ...client, seed_amount: seedAmount } : client
      )
    );
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: 'success',
        icon: Play,
        label: 'Activo',
        bgClass: 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300'
      },
      paused: {
        color: 'warning',
        icon: Pause,
        label: 'Pausado',
        bgClass: 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300'
      },
      completed: {
        color: 'primary',
        icon: CheckCircle,
        label: 'Completado',
        bgClass: 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
      }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bgClass}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const getRiskLevel = (currentStep, maxSteps) => {
    const percentage = (currentStep / maxSteps) * 100;
    if (percentage <= 40) return { level: 'Bajo', color: 'success', emoji: '🟢' };
    if (percentage <= 70) return { level: 'Medio', color: 'warning', emoji: '🟡' };
    return { level: 'Alto', color: 'danger', emoji: '🔴' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error al cargar evento
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {error?.response?.data?.error || 'No se pudo cargar la información del evento'}
        </p>
        <p className="text-xs text-gray-500 mt-2">
          EventId: {eventId} | Event: {event ? 'Loaded' : 'Not loaded'}
        </p>
      </div>
    );
  }

  // Debug: Log para verificar datos
  console.log('EventDetails Debug:', {
    eventId,
    event: event?.event,
    status: event?.event?.status,
    participants: event?.event?.participants,
    allClients: allClients
  });

  const eventData = event?.event || event;
  const risk = eventData?.status === 'active' ? getRiskLevel(eventData.current_step, eventData.max_steps) : null;

  const tabs = [
    { id: 'overview', label: 'Resumen', icon: Eye },
    { id: 'participants', label: 'Participantes', icon: Users },
    { id: 'matches', label: 'Partidos', icon: Activity },
    { id: 'statistics', label: 'Estadísticas', icon: BarChart3 },
    { id: 'calculator', label: 'Calculadora', icon: Calculator }
  ];

  // Agregar tab de liquidación solo si el evento está completado
  if (eventData?.status === 'completed') {
    tabs.push({ id: 'liquidation', label: 'Liquidación', icon: DollarSign });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start space-x-4 flex-1">
          <div className="w-12 h-12 bg-gradient-primary rounded-lg flex items-center justify-center flex-shrink-0">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {eventData.name}
              </h1>
              {getStatusBadge(eventData.status)}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {eventData.description || 'Evento de apuestas progresivas'}
            </p>
          </div>
        </div>
      </div>

      {/* Botones de Acción - Debajo en móvil */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
        {onEdit && (
          <button
            onClick={() => onEdit(eventData)}
            className="btn-secondary"
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </button>
        )}

        {/* Controles de Estado */}
        {eventData.status === 'active' && (
          <button
            onClick={() => handleStatusChange('paused')}
            className="btn-warning"
          >
            <Pause className="w-4 h-4 mr-2" />
            Pausar
          </button>
        )}

        {eventData.status === 'paused' && (
          <button
            onClick={() => handleStatusChange('active')}
            className="btn-success"
          >
            <Play className="w-4 h-4 mr-2" />
            Reanudar
          </button>
        )}

        {(eventData.status === 'active' || eventData.status === 'paused') && (
          <button
            onClick={() => handleStatusChange('completed')}
            className="btn-primary"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Completar
          </button>
        )}
      </div>

      {/* Progreso del Evento (solo si está activo) */}
      {eventData.status === 'active' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Progreso del Evento
            </h3>
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{risk.emoji}</span>
              <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                risk.color === 'success' ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300' :
                risk.color === 'warning' ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300' :
                'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
              }`}>
                Riesgo {risk.level}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Progreso</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                Paso {eventData.current_step} de {eventData.max_steps}
              </span>
            </div>

            <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-3">
              <div
                className="bg-gradient-primary h-3 rounded-full transition-all duration-500 relative overflow-hidden"
                style={{
                  width: `${(eventData.current_step / eventData.max_steps) * 100}%`
                }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>0%</span>
              <span>25%</span>
              <span>50%</span>
              <span>75%</span>
              <span>100%</span>
            </div>
          </div>
        </motion.div>
      )}

      {/* Estadísticas Rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Participantes
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {eventData.participant_count || 0}
              </p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Capital Gestionado
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {formatCurrency(eventData.total_capital_managed)}
              </p>
            </div>
            <div className="p-3 bg-success-100 dark:bg-success-900/30 rounded-lg">
              <DollarSign className="w-6 h-6 text-success-600 dark:text-success-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Paso Actual
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {eventData.status === 'active' ? eventData.current_step : '-'}
              </p>
            </div>
            <div className="p-3 bg-warning-100 dark:bg-warning-900/30 rounded-lg">
              <Target className="w-6 h-6 text-warning-600 dark:text-warning-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Duración por Paso
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {eventData.step_duration}s
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs de Navegación */}
      <div className="border-b border-gray-200 dark:border-dark-700 overflow-x-auto">
        <nav className="-mb-px flex flex-wrap md:flex-nowrap gap-2 md:space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenido de Tabs */}
      <div className="mt-6">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {/* Información del Evento */}
            <div className="card p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-primary rounded-lg">
                  <Info className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Información del Evento
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Fecha de inicio
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.start_date ? formatDate(eventData.start_date) : 'No definida'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Fecha de fin
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.end_date ? formatDate(eventData.end_date) : 'No definida'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Máximo de participantes
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.max_participants || 'Sin límite'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Rango de apuestas
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.min_bet_amount && eventData.max_bet_amount
                      ? `${formatCurrency(eventData.min_bet_amount)} - ${formatCurrency(eventData.max_bet_amount)}`
                      : 'No definido'}
                  </p>
                </div>
              </div>
            </div>

            {/* Configuración */}
            <div className="card p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                  <Settings className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Configuración
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Número máximo de pasos
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.max_steps || 13}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Duración por paso
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.step_duration || 300} segundos
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Comisión
                  </label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {eventData.commission_rate !== null && eventData.commission_rate !== undefined
                      ? `${Number(eventData.commission_rate).toFixed(2)}%`
                      : '0.00%'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
                    Fecha de creación
                  </label>
                  <p className="text-gray-900 dark:text-gray-100">
                    {eventData.created_at ? formatDate(eventData.created_at) : 'No disponible'}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'participants' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Header de Participantes */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-primary rounded-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Participantes del Evento
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {eventData?.participants?.length || 0} participante{(eventData?.participants?.length || 0) !== 1 ? 's' : ''} activos
                    </p>
                  </div>
                </div>

                {eventData?.status === 'active' && (
                  <button
                    onClick={() => setShowAddParticipant(true)}
                    className="btn-primary"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Participantes
                  </button>
                )}
              </div>

              {/* Lista de Participantes */}
              {(!eventData?.participants || eventData.participants.length === 0) ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No hay participantes
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Agrega clientes a este evento para comenzar
                  </p>
                  {eventData?.status === 'active' && (
                    <button
                      onClick={() => setShowAddParticipant(true)}
                      className="btn-primary"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Primer Participante
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {eventData.participants.map((participant, index) => (
                    <motion.div
                      key={participant.user_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 bg-gray-50 dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-700"
                    >
                      <div className="flex flex-col space-y-3">
                        {/* Header: Avatar + Name + Status */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="w-10 h-10 flex-shrink-0 bg-gradient-primary rounded-full flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {participant.full_name.charAt(0).toUpperCase()}
                              </span>
                            </div>

                            <div className="min-w-0 flex-1">
                              <h4 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {participant.full_name}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                {participant.email}
                              </p>
                            </div>
                          </div>

                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                            participant.is_active
                              ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                          }`}>
                            <UserCheck className="w-3 h-3 mr-1" />
                            {participant.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>

                        {/* Financial Info */}
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div>
                            <p className="text-gray-500 dark:text-gray-400 mb-1">Semilla</p>
                            {editingSeedUserId === participant.user_id ? (
                              <div className="flex items-center space-x-1">
                                <input
                                  type="number"
                                  value={editingSeedValue}
                                  onChange={(e) => setEditingSeedValue(e.target.value)}
                                  className="w-24 px-2 py-1 text-sm border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-dark-700 dark:border-dark-600 dark:text-gray-100"
                                  min="1"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleUpdateSeed(participant.user_id);
                                    if (e.key === 'Escape') { setEditingSeedUserId(null); setEditingSeedValue(''); }
                                  }}
                                />
                                <button
                                  onClick={() => handleUpdateSeed(participant.user_id)}
                                  disabled={updateSeedMutation.isLoading}
                                  className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                  title="Confirmar"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => { setEditingSeedUserId(null); setEditingSeedValue(''); }}
                                  className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded"
                                  title="Cancelar"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {formatCurrency(participant.seed_amount)}
                                </p>
                                {eventData?.status === 'active' && eventData?.current_step === 1 && participant.is_active && (
                                  <button
                                    onClick={() => {
                                      setEditingSeedUserId(participant.user_id);
                                      setEditingSeedValue(participant.seed_amount);
                                    }}
                                    className="p-1 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                                    title="Editar semilla (solo disponible en paso 1)"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-500 dark:text-gray-400 mb-1">Balance</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {formatCurrency(participant.current_balance)}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        {(eventData?.status === 'pending' || eventData?.status === 'active') && (
                          <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-dark-700">
                            {participant.is_active ? (
                              <button
                                onClick={() => handleRemoveParticipant(participant.user_id)}
                                className="flex items-center px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                title="Desactivar participante"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Eliminar
                              </button>
                            ) : (
                              <button
                                onClick={() => handleReactivateParticipant(participant.user_id)}
                                className="flex items-center px-3 py-1.5 text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                title="Reactivar participante (verificará saldo)"
                                disabled={reactivateParticipantMutation.isLoading}
                              >
                                <RefreshCw className={`w-3 h-3 mr-1 ${reactivateParticipantMutation.isLoading ? 'animate-spin' : ''}`} />
                                Reactivar
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal para Agregar Participantes */}
            {showAddParticipant && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                onClick={() => setShowAddParticipant(false)}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white dark:bg-dark-900 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Agregar Participantes al Evento
                      </h3>
                      <button
                        onClick={() => setShowAddParticipant(false)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Lista de Clientes Disponibles */}
                    <div className="space-y-4 mb-6">
                      <h4 className="font-medium text-gray-900 dark:text-gray-100">
                        Selecciona los clientes a agregar:
                      </h4>

                      {allClients?.users?.filter(client =>
                        !eventData?.participants?.some(p => p.user_id === client.id)
                      ).map(client => {
                        const isSelected = selectedClients.some(c => c.id === client.id);
                        const selectedClient = selectedClients.find(c => c.id === client.id);

                        return (
                          <div
                            key={client.id}
                            className={`p-4 border rounded-lg cursor-pointer transition-all ${
                              isSelected
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-gray-200 dark:border-dark-700 hover:border-gray-300'
                            }`}
                            onClick={() => toggleClientSelection(client)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center">
                                  <span className="text-white font-semibold text-xs">
                                    {client.full_name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-gray-100">
                                    {client.full_name}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {client.email}
                                  </p>
                                </div>
                              </div>

                              {isSelected && (
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-600 dark:text-gray-400">
                                    Semilla:
                                  </span>
                                  <input
                                    type="number"
                                    value={selectedClient?.seed_amount || 1000}
                                    onChange={(e) => updateClientSeedAmount(client.id, parseInt(e.target.value))}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-dark-600 rounded bg-white dark:bg-dark-800"
                                    min="100"
                                    step="100"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {allClients?.users?.filter(client =>
                        !eventData?.participants?.some(p => p.user_id === client.id)
                      ).length === 0 && (
                        <div className="text-center py-8">
                          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-600 dark:text-gray-400">
                            Todos los clientes ya están participando en este evento
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Botones de Acción */}
                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={() => setShowAddParticipant(false)}
                        className="btn-secondary"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleAddParticipants}
                        disabled={selectedClients.length === 0 || addParticipantMutation.isLoading}
                        className="btn-primary"
                      >
                        {addParticipantMutation.isLoading ? 'Agregando...' : `Agregar ${selectedClients.length} Cliente${selectedClients.length !== 1 ? 's' : ''}`}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        )}

        {activeTab === 'matches' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <EventMatches eventId={eventId} />
          </motion.div>
        )}

        {activeTab === 'statistics' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-6"
          >
            <EventStatistics eventId={eventId} />
          </motion.div>
        )}

        {activeTab === 'calculator' && (
          <BettingCalculator />
        )}

        {activeTab === 'liquidation' && eventData?.status === 'completed' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <EventLiquidation eventId={eventId} event={eventData} />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default EventDetails;